import { useEffect, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import API from '../api/api';
import WorkTrial from './WorkTrial';

/**
 * The per-trial engine switch, and the only thing that stands in front of the
 * legacy work-trial page.
 *
 * `/work-trial/:id` stays live because live links and emails point at it. Which
 * renderer it gets is decided per trial by the one field — `WorkTrial.eval_engine`,
 * `"eval3"` by default and stamped at trial creation — so an eval4 trial arriving
 * on an old link lands in its workspace and every other trial gets today's page,
 * unchanged and un-refactored.
 *
 * Nothing renders until the engine is known. Mounting the legacy page first and
 * redirecting after would be quicker, but it would also open the eval3 renderer
 * on an eval4 trial for long enough to log that it was opened, and that log is
 * evidence somebody is going to read.
 */
const WorkTrialRoute = () => {
    const { workTrialId } = useParams();
    const [engine, setEngine] = useState('');

    useEffect(() => {
        let cancelled = false;
        setEngine('');

        API.get(`work-trials/${workTrialId}`)
            .then((response) => {
                if (cancelled) return;
                setEngine(response.data?.work_trial?.eval_engine || 'eval3');
            })
            .catch(() => {
                // The legacy page has its own error state and its own retry; a
                // trial we could not read is one it should report, not one this
                // gate should guess an engine for.
                if (!cancelled) setEngine('eval3');
            });

        return () => { cancelled = true; };
    }, [workTrialId]);

    if (!engine) return null;
    if (engine === 'eval4') return <Navigate to={`/workspace/${workTrialId}/home`} replace />;

    return <WorkTrial />;
};

export default WorkTrialRoute;
